import {
  IconButton,
  Slide,
  Box,
  Typography,
  useTheme,
  Portal,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { Close, Refresh } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { UtilityService } from "../services/UtilityService";
import { useTranslation } from "react-i18next";

// Individual notification component
function NotificationItem({ notification, index, onClose }) {
  const { t } = useTranslation("common");
  const [progress, setProgress] = useState(100);
  const [isVisible, setIsVisible] = useState(false);
  const isMobile = useSelector((state) => state.isMobile);
  const theme = useTheme();

  // Calculate duration based on message length (minimum 3s, maximum 10s)
  const calculatedDuration = Math.min(
    Math.max(notification.message.length * 50, 3000),
    10000
  );

  useEffect(() => {
    setIsVisible(true);
    setProgress(100);

    // Start progress countdown
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress - 100 / (calculatedDuration / 100);
        if (newProgress <= 0) {
          clearInterval(interval);
          onClose(notification.id);
          return 0;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [notification.id, calculatedDuration, onClose]);

  // Enhanced close handler with haptic feedback
  const handleEnhancedClose = useCallback(
    (event, reason) => {
      if (reason === "clickaway") {
        return;
      }

      // Add subtle haptic feedback on mobile
      if (navigator.vibrate && isMobile) {
        navigator.vibrate(50);
      }

      onClose(notification.id);
    },
    [isMobile, notification.id, onClose]
  );

  // Action buttons for different message types
  const getActionButtons = useCallback(() => {
    const actions = [];

    // Add action buttons based on message content or severity
    if (
      notification.severity === "error" &&
      notification.message.toLowerCase().includes("network")
    ) {
      actions.push(
        <IconButton
          key="retry"
          size="small"
          aria-label={t("common.retry")}
          color="inherit"
          onClick={() => {
            window.location.reload();
          }}
          sx={{ mr: 1 }}
        >
          <Refresh fontSize="small" />
        </IconButton>
      );
    }

    actions.push(
      <IconButton
        key="close"
        size="small"
        aria-label={t("common.close")}
        color="inherit"
        onClick={handleEnhancedClose}
      >
        <Close fontSize="small" />
      </IconButton>
    );

    return actions;
  }, [notification.severity, notification.message, t, handleEnhancedClose]);

  return (
    <Slide
      direction="up"
      in={isVisible}
      mountOnEnter
      unmountOnExit
      timeout={{ enter: 300, exit: 200 }}
    >
      <Box
        sx={{
          minWidth: 320,
          maxWidth: 480,
          width: "100%",
          display: "flex",
          alignItems: "flex-start",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          mb: 1.5, // Space between stacked notifications
          backgroundColor: (() => {
            switch (notification.severity) {
              case "error":
                return theme.palette.error.main;
              case "warning":
                return theme.palette.warning.main;
              case "success":
                return theme.palette.success.main;
              default:
                return theme.palette.info.main;
            }
          })(),
          color: "white",
          transform: `translateY(${index * -8}px)`, // Slight offset for stacking effect
          zIndex: 1400 - index, // Higher z-index for newer notifications
          borderRadius: 3,
          padding: "16px 20px",
          position: "relative",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: `translateY(${index * -8 - 2}px) scale(1.02)`,
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        {/* Alert Icon */}
        <Box sx={{ display: "flex", alignItems: "center", mr: 2, mt: 0.25 }}>
          {notification.severity === "success" && (
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "bold",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(4px)",
              }}
            >
              ✓
            </Box>
          )}
          {notification.severity === "error" && (
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "bold",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(4px)",
              }}
            >
              ✕
            </Box>
          )}
          {notification.severity === "warning" && (
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "bold",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(4px)",
              }}
            >
              ⚠
            </Box>
          )}
          {notification.severity === "info" && (
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                fontWeight: "bold",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(4px)",
              }}
            >
              i
            </Box>
          )}
        </Box>

        {/* Message Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "white",
              mb: progress > 0 ? 2 : 0,
              fontSize: "15px",
              lineHeight: 1.4,
              letterSpacing: "0.01em",
            }}
          >
            {notification.message}
          </Typography>

          {progress > 0 && (
            <Box
              sx={{
                position: "relative",
                height: 4,
                borderRadius: 2,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${progress}%`,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  borderRadius: 2,
                  transition: "width 0.1s linear",
                  backgroundImage:
                    "linear-gradient(45deg, transparent 25%, rgba(255,255,255,0.2) 25%, rgba(255,255,255,0.2) 50%, transparent 50%, transparent 75%, rgba(255,255,255,0.2) 75%)",
                  backgroundSize: "20px 20px",
                  animation: "progressStripes 1s linear infinite",
                  "@keyframes progressStripes": {
                    "0%": {
                      backgroundPosition: "0 0",
                    },
                    "100%": {
                      backgroundPosition: "20px 0",
                    },
                  },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{ display: "flex", alignItems: "flex-start", ml: 2, mt: -0.5 }}
        >
          {getActionButtons().map((button, btnIndex) => (
            <Box
              key={btnIndex}
              sx={{
                "& .MuiIconButton-root": {
                  color: "rgba(255, 255, 255, 0.8)",
                  padding: "6px",
                  borderRadius: "8px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    color: "white",
                    transform: "scale(1.1)",
                  },
                },
              }}
            >
              {button}
            </Box>
          ))}
        </Box>
      </Box>
    </Slide>
  );
}

// Main notification container
export default function SnackbarNotification() {
  const snackbar = useSelector((state) => state.snackbar);

  const handleCloseNotification = useCallback((notificationId) => {
    UtilityService.handleSnackbarClose(notificationId);
  }, []);

  // Handle both old array format and new object format
  let activeNotifications = [];

  if (Array.isArray(snackbar)) {
    // Old format - snackbar is directly an array
    activeNotifications = snackbar;
  } else if (snackbar && snackbar.active) {
    // New format - snackbar has active property
    activeNotifications = snackbar.active;
  }

  if (!activeNotifications || activeNotifications.length === 0) {
    return null;
  }

  return (
    <Portal>
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1400,
          display: "flex",
          flexDirection: "column-reverse", // Stack newest on top
          alignItems: "center",
        }}
      >
        {activeNotifications.map((notification, index) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            index={index}
            onClose={handleCloseNotification}
          />
        ))}
      </Box>
    </Portal>
  );
}
